import { useId, CSSProperties } from 'react'

type GasType = 'hydrogen' | 'helium' | 'neon'

interface GasDischargeSetupProps {
  selectedTube?: GasType | null
  isPowered?: boolean
  onSelectTube?: (tube: GasType) => void
  onTogglePower?: () => void
  disabled?: boolean
  className?: string
}

// Gas tube colors
const GAS_COLORS: Record<GasType, { glow: string; label: string; symbol: string }> = {
  hydrogen: { glow: '#FF69B4', label: 'Hydrogen', symbol: 'H' },
  helium: { glow: '#FFA500', label: 'Helium', symbol: 'He' },
  neon: { glow: '#FF4500', label: 'Neon', symbol: 'Ne' }
}

const GasDischargeSetup = ({
  selectedTube = null,
  isPowered = false,
  onSelectTube,
  onTogglePower,
  disabled = false,
  className = ''
}: GasDischargeSetupProps) => {
  const id = useId()

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  const tubes: GasType[] = ['hydrogen', 'helium', 'neon']

  return (
    <svg
      viewBox="0 0 280 200"
      width={350}
      height={280}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Metal gradient */}
        <linearGradient id={`metal-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3A3A3A" />
          <stop offset="30%" stopColor="#5A5A5A" />
          <stop offset="70%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#3A3A3A" />
        </linearGradient>

        {/* Glass gradient */}
        <linearGradient id={`glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(200,220,240,0.5)" />
          <stop offset="20%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="80%" stopColor="rgba(255,255,255,0.3)" />
          <stop offset="100%" stopColor="rgba(200,220,240,0.4)" />
        </linearGradient>

        {/* Power supply gradient */}
        <linearGradient id={`psu-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#4A4A4A" />
          <stop offset="50%" stopColor="#3A3A3A" />
          <stop offset="100%" stopColor="#2A2A2A" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-5%" y="-5%" width="115%" height="115%">
          <feDropShadow dx="2" dy="3" stdDeviation="2" floodOpacity="0.25" />
        </filter>

        {/* Glow filters for each gas */}
        {tubes.map((gas) => (
          <filter key={gas} id={`glow-${gas}-${id}`} x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feFlood floodColor={GAS_COLORS[gas].glow} floodOpacity="0.8" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="colorBlur" />
            <feMerge>
              <feMergeNode in="colorBlur" />
              <feMergeNode in="colorBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        ))}
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Tube rack / stand */}
        <g>
          {/* Base */}
          <rect
            x="30"
            y="140"
            width="160"
            height="15"
            fill={`url(#metal-${id})`}
            rx="3"
          />

          {/* Vertical supports */}
          <rect x="40" y="50" width="8" height="90" fill={`url(#metal-${id})`} rx="2" />
          <rect x="172" y="50" width="8" height="90" fill={`url(#metal-${id})`} rx="2" />

          {/* Top bar with slots */}
          <rect
            x="35"
            y="45"
            width="150"
            height="12"
            fill={`url(#metal-${id})`}
            rx="3"
          />

          {/* Tube slots */}
          {tubes.map((gas, i) => (
            <rect
              key={gas}
              x={55 + i * 45}
              y="43"
              width="30"
              height="8"
              fill="#1A1A1A"
              rx="2"
            />
          ))}
        </g>

        {/* Discharge tubes */}
        {tubes.map((gas, i) => {
          const x = 70 + i * 45
          const isSelected = selectedTube === gas
          const isGlowing = isSelected && isPowered
          const gasConfig = GAS_COLORS[gas]

          return (
            <g
              key={gas}
              style={{
                cursor: disabled ? 'default' : onSelectTube ? 'pointer' : 'default'
              }}
              onClick={() => !disabled && onSelectTube && onSelectTube(gas)}
            >
              {/* Tube body */}
              <rect
                x={x - 8}
                y="55"
                width="16"
                height="80"
                fill={isGlowing ? gasConfig.glow : `url(#glass-${id})`}
                stroke={isSelected ? '#FF5A36' : '#7A9BB8'}
                strokeWidth={isSelected ? 2 : 1}
                rx="8"
                filter={isGlowing ? `url(#glow-${gas}-${id})` : undefined}
                opacity={isGlowing ? 0.9 : 1}
              />

              {/* Inner glow when powered */}
              {isGlowing && (
                <rect
                  x={x - 5}
                  y="60"
                  width="10"
                  height="70"
                  fill={gasConfig.glow}
                  rx="5"
                  opacity="0.8"
                >
                  <animate
                    attributeName="opacity"
                    values="0.8;0.5;0.8"
                    dur="0.5s"
                    repeatCount="indefinite"
                  />
                </rect>
              )}

              {/* Electrodes */}
              <rect
                x={x - 4}
                y="52"
                width="8"
                height="8"
                fill="#8B8B8B"
                rx="1"
              />
              <rect
                x={x - 4}
                y="130"
                width="8"
                height="8"
                fill="#8B8B8B"
                rx="1"
              />

              {/* Wire connections */}
              {isSelected && (
                <g>
                  <path
                    d={`M${x} 52 L${x} 40 L210 40 L210 85`}
                    fill="none"
                    stroke="#333"
                    strokeWidth="2"
                  />
                  <path
                    d={`M${x} 138 L${x} 150 L210 150 L210 115`}
                    fill="none"
                    stroke="#333"
                    strokeWidth="2"
                  />
                </g>
              )}

              {/* Gas label */}
              <text
                x={x}
                y="165"
                fontSize="8"
                fill={isSelected ? '#FF5A36' : '#888'}
                textAnchor="middle"
                fontWeight={isSelected ? '600' : '400'}
              >
                {gasConfig.symbol}
              </text>
            </g>
          )
        })}

        {/* Power Supply Unit */}
        <g transform="translate(200, 60)">
          {/* Main body */}
          <rect
            x="0"
            y="0"
            width="70"
            height="100"
            fill={`url(#psu-${id})`}
            stroke="#4A4A4A"
            strokeWidth="1"
            rx="5"
          />

          {/* Label */}
          <rect
            x="5"
            y="5"
            width="60"
            height="15"
            fill="#1A1A1A"
            rx="2"
          />
          <text
            x="35"
            y="15"
            fontSize="8"
            fill="#00A86B"
            textAnchor="middle"
            fontWeight="600"
          >
            HIGH VOLTAGE
          </text>

          {/* Voltage display */}
          <rect
            x="10"
            y="25"
            width="50"
            height="20"
            fill="#001a00"
            rx="2"
          />
          <text
            x="35"
            y="40"
            fontSize="12"
            fill={isPowered ? '#00FF00' : '#003300'}
            textAnchor="middle"
            fontFamily="'Courier New', monospace"
            fontWeight="bold"
          >
            {isPowered ? '5000V' : '----'}
          </text>

          {/* Output terminals */}
          <g>
            <circle cx="20" cy="55" r="5" fill="#B22222" stroke="#8B0000" strokeWidth="1" />
            <text x="20" y="58" fontSize="6" fill="white" textAnchor="middle">+</text>
            <circle cx="50" cy="55" r="5" fill="#2F2F2F" stroke="#1A1A1A" strokeWidth="1" />
            <text x="50" y="58" fontSize="6" fill="white" textAnchor="middle">-</text>
          </g>

          {/* Power button */}
          <g
            style={{
              cursor: disabled ? 'default' : onTogglePower ? 'pointer' : 'default'
            }}
            onClick={() => !disabled && onTogglePower && onTogglePower()}
          >
            <rect
              x="15"
              y="70"
              width="40"
              height="20"
              fill={isPowered ? '#00A86B' : '#4A4A4A'}
              stroke={isPowered ? '#008B5B' : '#3A3A3A'}
              strokeWidth="2"
              rx="4"
            />
            <text
              x="35"
              y="84"
              fontSize="9"
              fill="white"
              textAnchor="middle"
              fontWeight="600"
            >
              {isPowered ? 'ON' : 'OFF'}
            </text>
          </g>

          {/* Power LED */}
          <circle
            cx="60"
            cy="80"
            r="4"
            fill={isPowered ? '#00FF00' : '#333'}
          >
            {isPowered && (
              <animate
                attributeName="opacity"
                values="1;0.6;1"
                dur="1s"
                repeatCount="indefinite"
              />
            )}
          </circle>
        </g>

        {/* Warning label */}
        <g transform="translate(200, 165)">
          <rect
            x="0"
            y="0"
            width="70"
            height="18"
            fill="#FFD700"
            rx="2"
          />
          <text
            x="35"
            y="6"
            fontSize="6"
            fill="#000"
            textAnchor="middle"
            fontWeight="bold"
          >
            WARNING
          </text>
          <text
            x="35"
            y="14"
            fontSize="5"
            fill="#000"
            textAnchor="middle"
          >
            HIGH VOLTAGE
          </text>
        </g>
      </g>

      {/* Tube labels */}
      <g>
        {tubes.map((gas, i) => (
          <text
            key={gas}
            x={70 + i * 45}
            y="178"
            fontSize="7"
            fill="#666"
            textAnchor="middle"
          >
            {GAS_COLORS[gas].label}
          </text>
        ))}
      </g>

      {/* Label */}
      <text
        x="140"
        y="195"
        fontSize="10"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Gas Discharge Tubes
      </text>
    </svg>
  )
}

export default GasDischargeSetup
